/**
 * Monthly sale incentive (OE @ rate%, ordinary net @ rate%).
 * Sales return OE vs ordinary: manual upload fields when present; else split rAmt by OE/ordinary sales mix.
 */

export const DEFAULT_MISC_DIVISOR = 8;
export const DEFAULT_OE_RATE_PCT = 0.5;
export const DEFAULT_ORD_RATE_PCT = 1;

/**
 * @param {object} data - merged monthly sale page data
 * @param {string} brandId
 * @param {object} brandRow - brands[] row { oeAmt, othAmt, rAmt }
 * @returns {{ oe: number, ord: number }}
 */
export function oeOrdSaleReturnAmounts(data, brandId, brandRow) {
  let oe = Math.max(0, Number(data?.saleReturnOeByBrand?.[brandId]) || 0);
  let ord = Math.max(0, Number(data?.saleReturnOrdByBrand?.[brandId]) || 0);
  const manualSum = oe + ord;
  const rAmt = Math.max(0, Number(brandRow?.rAmt) || 0);
  if (manualSum <= 0 && rAmt > 0) {
    const grossOe = grossOeSaleForBrand(brandRow);
    const grossOrd = grossOrdinarySaleForBrand(brandRow);
    const tot = grossOe + grossOrd;
    if (tot > 0) {
      oe = rAmt * (grossOe / tot);
      ord = rAmt * (grossOrd / tot);
    } else {
      oe = 0;
      ord = rAmt;
    }
  }
  return { oe, ord };
}

/** Gap between brand current-month total sale (`c`) and report OE + OTHER columns. */
export function brandSaleRemainder(brandRow) {
  const total = Math.max(0, Number(brandRow?.c) || 0);
  const oe = Math.max(0, Number(brandRow?.oeAmt) || 0);
  const ord = Math.max(0, Number(brandRow?.othAmt) || 0);
  return Math.max(0, total - oe - ord);
}

/** OE sale for incentive — OE column from import report, capped at brand total. */
export function grossOeSaleForBrand(brandRow) {
  const total = Math.max(0, Number(brandRow?.c) || 0);
  const oe = Math.max(0, Number(brandRow?.oeAmt) || 0);
  return total > 0 ? Math.min(oe, total) : oe;
}

/**
 * Ordinary sale for incentive: brand current-month total minus OE (includes any gap vs othAmt).
 * Blank-brand / misc share (÷ 8) is added in computeOrdinaryIncentive, not in this gross column.
 */
export function grossOrdinarySaleForBrand(brandRow) {
  const total = Math.max(0, Number(brandRow?.c) || 0);
  const oe = grossOeSaleForBrand(brandRow);
  return Math.max(0, total - oe);
}

/**
 * Per-brand share of blank-brand misc + unassigned ordinary (÷ divisor, default 8 brands).
 * Folded into ordinary net incentive — no separate column.
 */
export function incentiveOrdinaryMiscSharePerBrand(data, brandMap, incentiveBrands, divisor = DEFAULT_MISC_DIVISOR) {
  return incentiveMainMiscPoolSummary(data, brandMap, incentiveBrands, divisor).sharePerBrand;
}

/** Blank-brand misc pool split across incentive brands (default ÷ 8). */
export function incentiveMainMiscPoolSummary(data, brandMap, incentiveBrands, divisor = DEFAULT_MISC_DIVISOR) {
  const div = Math.max(1, Number(divisor) || DEFAULT_MISC_DIVISOR);
  const miscSale = Math.max(0, Number(data?.miscSale) || 0);
  let ordFromBrands = 0;
  for (const bid of incentiveBrands || []) {
    const row = brandMap?.get?.(bid);
    if (row) ordFromBrands += grossOrdinarySaleForBrand(row);
  }
  const ordBlankRemainder = Math.max(0, (Number(data?.ordinaryAmt) || 0) - ordFromBrands);
  const miscPoolTotal = miscSale + ordBlankRemainder;
  const sharePerBrand = miscPoolTotal / div;
  return { miscSale, ordBlankRemainder, miscPoolTotal, sharePerBrand, divisor: div };
}

/**
 * @param {number} oeSale
 * @param {number} branchCutOe
 * @param {number} oeReturn
 * @param {number} minOe
 * @param {number} oeRatePct - percent, e.g. 0.5 for half percent
 */
export function computeOeIncentive(oeSale, branchCutOe, oeReturn, minOe, oeRatePct) {
  const net = Number(oeSale) - Number(branchCutOe) - Number(oeReturn) - Number(minOe);
  const netClamped = Math.max(0, net);
  const rate = Number(oeRatePct);
  const r = Number.isFinite(rate) ? rate : DEFAULT_OE_RATE_PCT;
  return { netOe: net, netOeForRate: netClamped, oeIncentive: netClamped * (r / 100) };
}

/**
 * @param {number} ordSale
 * @param {number} branchCutOrd
 * @param {number} miscShare - e.g. (miscSale + blank ordinary) / 8
 * @param {number} ordReturn
 * @param {number} minOrd
 * @param {number} ordRatePct - percent on ordinary net (e.g. 1 for 1%)
 */
export function computeOrdinaryIncentive(ordSale, branchCutOrd, miscShare, ordReturn, minOrd, ordRatePct) {
  const net =
    Number(ordSale) - Number(branchCutOrd) + Number(miscShare) - Number(ordReturn) - Number(minOrd);
  const netClamped = Math.max(0, net);
  const rate = Number(ordRatePct);
  const r = Number.isFinite(rate) ? rate : DEFAULT_ORD_RATE_PCT;
  return { netOrd: net, netOrdForRate: netClamped, ordIncentive: netClamped * (r / 100) };
}
